<script setup lang="ts">
import { Components } from '@/api/generated/api';
import ChevronDownIcon from '@/assets/icons/ChevronDownIcon.vue';
import { TSchoolSubjectWithGroupId } from '@/composables/schoolSubjectGroups/useSchoolSubjectGroup';
import SchoolSubjectBadge from '@/core/elements/SchoolSubjectBadge.vue';
import SchoolSubjectSearchSelectDialog from '@/core/school-subjects/SchoolSubjectSearchSelectDialog.vue';
import { ISelectOption } from '@/interfaces';
import { toRecord } from 'common/utils/array.utils';
import { computed, toRefs, useTemplateRef } from 'vue';

const emit = defineEmits<{
  'update:modelValue': [value: number[]];
}>();

const props = withDefaults(
  defineProps<{
    availableSchoolSubjectGroups: Components.Schemas.SchoolSubjectGroupDto[];
    allSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
    visibleSubjects?: Components.Schemas.SchoolSubjectDto[];
    modelValue: number[];
    disabled?: boolean;
    label?: string;
    theme?: 'button' | 'input';
    canSelectMultiple?: boolean;
    error?: boolean;
  }>(),
  {
    theme: 'button',
    label: undefined,
    canSelectMultiple: false,
    error: false,
    visibleSubjects: undefined,
  },
);
const { availableSchoolSubjectGroups, allSchoolSubjects, modelValue, visibleSubjects } = toRefs(props);

const buttonRef = useTemplateRef<HTMLButtonElement | null>('buttonRef');
const schoolSubjectSearchSelectDialogRef = useTemplateRef('schoolSubjectSearchSelectDialogRef');

const toggleMenu = (): void => {
  schoolSubjectSearchSelectDialogRef.value?.toggleMenu();
};

const isLabelFloating = computed<boolean>(() => {
  return !!firstSelectedSchoolSubject.value;
});

const firstSelectedSchoolSubject = computed<Components.Schemas.SchoolSubjectDto | null>(() => {
  return modelValue.value.length ? (schoolSubjectById.value[modelValue.value[0]] ?? null) : null;
});

const schoolSubjectById = computed<Record<number, Components.Schemas.SchoolSubjectDto>>(() => {
  return toRecord(
    allSchoolSubjects.value,
    (subject) => subject.id,
    (subject) => subject,
  );
});

const schoolSubjectOptionsWithoutGroup = computed<ISelectOption<Components.Schemas.SchoolSubjectDto>[]>(() => {
  return allSchoolSubjects.value
    .filter((schoolSubject) => !schoolSubject.groupId)
    .sort((a, b) => a.order - b.order)
    .map((s) => ({
      label: s.name,
      value: s,
    }));
});

const schoolSubjectOptionsByGroupId = computed<Record<number, ISelectOption<TSchoolSubjectWithGroupId>[]>>(() => {
  const schoolSubjectsWithGroup = allSchoolSubjects.value
    .filter((schoolSubject): schoolSubject is TSchoolSubjectWithGroupId => !!schoolSubject.groupId)
    .sort((a, b) => a.order - b.order);

  return toRecord(
    schoolSubjectsWithGroup,
    (schoolSubject) => schoolSubject.groupId,
    (schoolSubject, existing) => [...(existing ?? []), { label: schoolSubject.name, value: schoolSubject }],
  );
});

const allOptions = computed<ISelectOption<Components.Schemas.SchoolSubjectDto>[]>(() => {
  return [...schoolSubjectOptionsWithoutGroup.value, ...Object.values(schoolSubjectOptionsByGroupId.value).flat()];
});

const selectOption = (value: number[]): void => {
  emit('update:modelValue', value);
};

const isSelected = (option: ISelectOption<Components.Schemas.SchoolSubjectDto>): boolean => {
  return modelValue.value.includes(option.value.id);
};

const isButtonSelected = computed<boolean>(() => {
  if (!modelValue.value.length) return false;

  if (!visibleSubjects.value) {
    return allOptions.value.some((o) => isSelected(o));
  }

  const visibleSubjectIds = new Set(visibleSubjects.value.map((s) => s.id));
  return allOptions.value.some((o) => isSelected(o) && !visibleSubjectIds.has(o.value.id));
});
</script>

<template>
  <div class="min-w-0 grid">
    <button
      v-if="theme === 'button'"
      ref="buttonRef"
      type="button"
      :disabled="disabled"
      :class="[
        'flex h-subject-badge-lg min-w-subject-badge-lg items-center gap-xs rounded-badge px-smd',
        'outline-inner transition duration-300 hover:brightness-95 active:brightness-90',
        disabled && 'opacity-50 pointer-events-none',
        isButtonSelected ? 'bg-tint text-on-tint' : 'bg-surface-tertiary text-primary',
      ]"
      @click="toggleMenu"
    >
      <span class="text-body font-semibold">
        {{ $t('general.more') }}
      </span>
      <ChevronDownIcon :class="['size-icon-xs shrink-0', isButtonSelected ? 'text-on-tint' : 'text-secondary']" />
    </button>

    <button
      v-if="theme === 'input'"
      ref="buttonRef"
      type="button"
      :disabled="disabled"
      :class="[
        'w-full min-w-0 relative state-effects',
        'input-text bg-white',
        label && 'input-text-labeled',
        ' !pr-8',
        firstSelectedSchoolSubject && 'pl-14',
        error && 'border-error',
      ]"
      @click="toggleMenu"
    >
      <label
        v-if="label"
        :class="[
          'input-text-label',
          firstSelectedSchoolSubject ? '!left-14' : '!left-[32px]',
          isLabelFloating && 'label-floating',
        ]"
      >
        {{ label }}
      </label>
      <div class="flex items-center gap-sm">
        <SchoolSubjectBadge
          v-if="firstSelectedSchoolSubject"
          :school-subject="firstSelectedSchoolSubject"
          class="absolute left-[10px] top-1/2 -translate-y-1/2"
        />
        <div v-if="firstSelectedSchoolSubject || !label" class="min-w-0 truncate text-body">
          {{
            firstSelectedSchoolSubject
              ? firstSelectedSchoolSubject.name
              : allSchoolSubjects.length
                ? $t('accessRights.pleaseSelect')
                : $t('schoolSubjects.emptyStateTitle')
          }}
        </div>
        <div v-if="modelValue.length > 1" :class="['text-body whitespace-nowrap']">+ {{ modelValue.length - 1 }}</div>
        <ChevronDownIcon
          :class="['absolute right-[13px] top-1/2 -translate-y-1/2', 'shrink-0 size-icon-sm', 'text-secondary']"
        />
      </div>
    </button>

    <SchoolSubjectSearchSelectDialog
      ref="schoolSubjectSearchSelectDialogRef"
      :anchor-element="buttonRef"
      :model-value="modelValue"
      :can-select-multiple="canSelectMultiple"
      :available-school-subject-groups="availableSchoolSubjectGroups"
      :all-school-subjects="allSchoolSubjects"
      @update:model-value="selectOption"
    />
  </div>
</template>
